/**
 * Mock File Upload Trigger Utility
 *
 * 通用的文件上传触发工具
 * 用于在开发环境中模拟用户上传文件
 *
 * @module mockFileUploadTrigger
 */

import { delay } from ".";

export interface TriggerFileUploadOptions {
  /** 文件URL，默认为 /icon.png */
  fileUrl?: string;
  /** 生成的文件名，默认为 mock-file.png */
  fileName?: string;
  /** 选择器，用于查找特定的文件上传控件 */
  selector?: string;
  /** 成功回调 */
  onSuccess?: (file: File) => void;
  /** 失败回调 */
  onError?: (error: Error) => void;
}

/**
 * 触发文件上传控件
 * 模拟用户选择文件并上传
 *
 * @param options - 配置选项
 * @returns Promise<boolean> - 是否成功触发
 */
export async function triggerFileUpload(
  options: TriggerFileUploadOptions = {}
): Promise<boolean> {
  const {
    fileUrl = "/icon.png",
    fileName = "mock-file.png",
    selector = 'input[type="file"]',
    onSuccess,
    onError,
  } = options;

  try {
    // 1. 查找文件上传控件
    const fileInput = document.querySelector(selector) as HTMLInputElement;

    if (!fileInput) {
      const error = new Error("未找到文件上传控件");
      onError?.(error);
      console.error("❌ 未找到文件上传控件，selector:", selector);
      return false;
    }

    // 2. 从指定URL获取文件
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`获取文件失败: ${response.statusText}`);
    }

    const blob = await response.blob();
    const file = new File([blob], fileName, { type: blob.type });

    // 3. 创建 DataTransfer 对象并添加文件
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    // 4. 将文件设置到 input 元素
    fileInput.files = dataTransfer.files;

    // 5. 触发多个事件以确保兼容性
    // 触发 input 事件（原生事件）
    const inputEvent = new Event("input", { bubbles: true });
    fileInput.dispatchEvent(inputEvent);

    // 触发 change 事件（原生事件）
    const changeEvent = new Event("change", { bubbles: true });
    fileInput.dispatchEvent(changeEvent);

    // 创建并触发 React 合成事件需要的属性
    const nativeEvent = new Event("change", { bubbles: true });
    Object.defineProperty(nativeEvent, "target", {
      writable: false,
      value: fileInput,
    });

    // 直接调用 React 的 onChange（如果存在）
    const reactProps = Object.keys(fileInput).find((key) =>
      key.startsWith("__reactProps")
    );
    if (reactProps && (fileInput as any)[reactProps]?.onChange) {
      (fileInput as any)[reactProps].onChange({
        target: fileInput,
        currentTarget: fileInput,
      });
    }

    console.log("✅ 已触发文件上传:", file);
    onSuccess?.(file);
    return true;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    onError?.(err);
    console.error("❌ 触发文件上传失败:", error);
    return false;
  }
}

/**
 * 批量触发多个文件上传控件
 *
 * @param selectors - 选择器数组
 * @param options - 配置选项（不包含selector）
 * @returns Promise<number> - 成功触发的数量
 */
export async function triggerMultipleFileUploads(
  selectors: string[],
  options: Omit<TriggerFileUploadOptions, "selector"> = {}
): Promise<number> {
  let successCount = 0;

  for (const selector of selectors) {
    const success = await triggerFileUpload({
      ...options,
      selector,
    });

    if (success) {
      successCount++;
      // 添加延迟，避免同时触发太多上传
      await delay(100);
    }
  }

  return successCount;
}

/**
 * 根据data-testid触发文件上传
 *
 * @param testId - data-testid 的值
 * @param options - 配置选项（不包含selector）
 * @returns Promise<boolean> - 是否成功触发
 */
export async function triggerFileUploadByTestId(
  testId: string,
  options: Omit<TriggerFileUploadOptions, "selector"> = {}
): Promise<boolean> {
  return triggerFileUpload({
    ...options,
    selector: `input[type="file"][data-testid="${testId}"]`,
  });
}

/**
 * 触发所有文件上传控件
 *
 * @param options - 配置选项（不包含selector）
 * @returns Promise<number> - 成功触发的数量
 */
export async function triggerAllFileUploads(
  options: Omit<TriggerFileUploadOptions, "selector"> = {}
): Promise<number> {
  const fileInputs = document.querySelectorAll(
    'input[type="file"]'
  ) as NodeListOf<HTMLInputElement>;

  let successCount = 0;

  for (let i = 0; i < fileInputs.length; i++) {
    const uniqueSelector = `input[type="file"]:nth-of-type(${i + 1})`;

    const success = await triggerFileUpload({
      ...options,
      selector: uniqueSelector,
    });

    if (success) {
      successCount++;
      // 添加延迟，避免同时触发太多上传
      await delay(100);
    }
  }

  return successCount;
}
